'use client';

import { useState, useEffect, useRef, useCallback } from 'react';
import { Search, X, Loader2 } from 'lucide-react';
import { useRouter, usePathname } from 'next/navigation';
import { cn } from '@/lib/utils';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { useDebounce } from '@/lib/hooks/useDebounce';

interface SearchSuggestion {
  type: 'recent' | 'popular' | 'suggestion';
  text: string;
  icon?: React.ReactNode;
}

export default function DashboardSearchBar() {
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [suggestions, setSuggestions] = useState<SearchSuggestion[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const router = useRouter();
  const pathname = usePathname();
  const inputRef = useRef<HTMLInputElement>(null);
  const debouncedQuery = useDebounce(query, 300);

  // Example suggestions - in production, these would come from an API
  const defaultSuggestions: SearchSuggestion[] = [
    { type: 'popular', text: 'positive feedback from clients' },
    { type: 'popular', text: 'enterprise features discussion' },
    { type: 'popular', text: 'pricing conversations' },
    { type: 'recent', text: 'team meetings this week' },
    { type: 'recent', text: 'product demo highlights' },
  ];

  // Fetch suggestions based on query
  useEffect(() => {
    const fetchSuggestions = async () => {
      if (!debouncedQuery || debouncedQuery.length < 2) {
        // Fetch default suggestions when no query
        try {
          const response = await fetch('/api/search/moments?prefix=');
          if (response.ok) {
            const data = await response.json();
            const apiSuggestions = data.suggestions?.map((text: string) => ({
              type: 'popular' as const,
              text
            })) || [];
            setSuggestions(apiSuggestions.length > 0 ? apiSuggestions : defaultSuggestions);
          } else {
            setSuggestions(defaultSuggestions);
          }
        } catch (error) {
          console.error('Error fetching suggestions:', error);
          setSuggestions(defaultSuggestions);
        }
        return;
      }

      setIsLoading(true);
      try {
        const response = await fetch(`/api/search/moments?prefix=${encodeURIComponent(debouncedQuery)}`);
        if (response.ok) {
          const data = await response.json();
          const apiSuggestions = data.suggestions?.map((text: string) => ({
            type: 'suggestion' as const,
            text
          })) || [];
          
          // Add query as a suggestion if it doesn't match existing ones
          if (apiSuggestions.length === 0 || !apiSuggestions.some((s: any) => s.text === debouncedQuery)) {
            apiSuggestions.unshift({
              type: 'suggestion',
              text: debouncedQuery
            });
          }
          
          setSuggestions(apiSuggestions);
        } else {
          // Fallback to filtering default suggestions
          const filtered = defaultSuggestions.filter(s => 
            s.text.toLowerCase().includes(debouncedQuery.toLowerCase())
          );
          
          if (filtered.length === 0 || !filtered.some(s => s.text === debouncedQuery)) {
            filtered.unshift({
              type: 'suggestion',
              text: debouncedQuery
            });
          }
          
          setSuggestions(filtered);
        }
      } catch (error) {
        console.error('Error fetching suggestions:', error);
        setSuggestions([{ type: 'suggestion', text: debouncedQuery }]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchSuggestions();
  }, [debouncedQuery]);

  const handleSearch = useCallback((searchQuery: string) => {
    if (!searchQuery.trim()) return;
    
    // Navigate to search results with query
    const searchParams = new URLSearchParams({ q: searchQuery });
    router.push(`/dashboard/moments?${searchParams.toString()}`);
    setIsOpen(false);
    setQuery('');
    
    // Blur the input to prevent focus issues
    inputRef.current?.blur();
  }, [router]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && query.trim()) {
      handleSearch(query);
    }
  };

  // Keyboard shortcut (Cmd/Ctrl + K)
  useEffect(() => {
    const handleKeyboardShortcut = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        e.stopPropagation();
        
        // Small delay to ensure focus works properly
        setTimeout(() => {
          inputRef.current?.focus();
          inputRef.current?.select();
        }, 50);
      }
    };

    document.addEventListener('keydown', handleKeyboardShortcut);
    return () => document.removeEventListener('keydown', handleKeyboardShortcut);
  }, []);

  // Debug logging
  useEffect(() => {
    console.log('[DashboardSearchBar] Component mounted, pathname:', pathname);
  }, [pathname]);

  // Only show search bar on dashboard pages
  if (!pathname?.startsWith('/dashboard')) {
    console.log('[DashboardSearchBar] Not showing - pathname:', pathname);
    return null;
  }

  // Handle click outside to close popover
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest('[data-search-container]')) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  return (
    <div className="relative w-full" data-search-container>
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              ref={inputRef}
              type="search"
              placeholder="Search videos, moments, transcripts... (⌘K)"
              value={query}
              onChange={(e) => {
                const value = e.target.value;
                setQuery(value);
                // Open popover when user starts typing
                if (value && !isOpen) {
                  setIsOpen(true);
                }
              }}
              onKeyDown={handleKeyDown}
              onFocus={() => {
                setIsFocused(true);
                // Only open popover if we have content to show
                if (query || suggestions.length > 0) {
                  setIsOpen(true);
                }
              }}
              onBlur={(e) => {
                // Don't blur if clicking within the popover
                const relatedTarget = e.relatedTarget as HTMLElement;
                if (relatedTarget?.closest('[role="dialog"]')) {
                  e.preventDefault();
                  inputRef.current?.focus();
                  return;
                }
                setIsFocused(false);
              }}
              className={cn(
                "pl-10 pr-10 h-9 w-full bg-muted/50 border-muted",
                "focus:bg-background focus:border-primary/20",
                "transition-all duration-200",
                isFocused && "ring-1 ring-primary/20"
              )}
            />
            {query && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setQuery('')}
                className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 p-0"
              >
                <X className="h-3 w-3" />
              </Button>
            )}
          </div>
        </PopoverTrigger>
        
        <PopoverContent 
          className="w-[var(--radix-popover-trigger-width)] p-0"
          align="start"
          sideOffset={5}
          onOpenAutoFocus={(e) => {
            // Prevent popover from stealing focus
            e.preventDefault();
          }}
          onInteractOutside={(e) => {
            // Prevent closing when clicking the input
            const target = e.target as HTMLElement;
            if (target.closest('[data-search-container]')) {
              e.preventDefault();
            }
          }}
        >
          <Command>
            <CommandList>
              {isLoading ? (
                <CommandEmpty>
                  <div className="flex items-center justify-center py-6">
                    <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                  </div>
                </CommandEmpty>
              ) : suggestions.length === 0 ? (
                <CommandEmpty>No results found.</CommandEmpty>
              ) : (
                <>
                  {query && (
                    <CommandGroup heading="Search for">
                      <CommandItem
                        onSelect={() => handleSearch(query)}
                        className="cursor-pointer"
                      >
                        <Search className="mr-2 h-4 w-4" />
                        {query}
                      </CommandItem>
                    </CommandGroup>
                  )}
                  
                  {suggestions.filter(s => s.type === 'recent').length > 0 && (
                    <CommandGroup heading="Recent searches">
                      {suggestions
                        .filter(s => s.type === 'recent')
                        .map((suggestion, index) => (
                          <CommandItem
                            key={index}
                            onSelect={() => handleSearch(suggestion.text)}
                            className="cursor-pointer"
                          >
                            <Search className="mr-2 h-4 w-4 text-muted-foreground" />
                            {suggestion.text}
                          </CommandItem>
                        ))}
                    </CommandGroup>
                  )}
                  
                  {suggestions.filter(s => s.type === 'popular').length > 0 && (
                    <CommandGroup heading="Popular searches">
                      {suggestions
                        .filter(s => s.type === 'popular')
                        .map((suggestion, index) => (
                          <CommandItem
                            key={index}
                            onSelect={() => handleSearch(suggestion.text)}
                            className="cursor-pointer"
                          >
                            <Search className="mr-2 h-4 w-4 text-muted-foreground" />
                            {suggestion.text}
                          </CommandItem>
                        ))}
                    </CommandGroup>
                  )}
                </>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}